import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { PromptDialog } from '@/components/ui/PromptDialog';
import { cn } from '@/components/ui/cn';
import * as api from '@/features/backlog/api';
import { FilterState, SavedFilter } from '@/features/backlog/types';
import { sanitizeFilters } from '@/features/backlog/filter';

/** Shared, named filter views for a board (stored server-side). */
export default function SavedViews({
  boardId,
  filters,
  onApply,
}: {
  boardId: string;
  filters: FilterState;
  onApply: (f: FilterState) => void;
}) {
  const [views, setViews] = useState<SavedFilter[]>([]);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const load = () => api.listFilters(boardId).then(setViews).catch(() => setViews([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opening the save dialog closes (unmounts) the dropdown that held focus, so Modal's own
  // focus-restore has nothing live to return to. Return focus to the always-mounted "Views"
  // trigger ourselves whenever the dialog closes.
  const closeSave = () => {
    setSaveOpen(false);
    triggerRef.current?.focus();
  };
  const save = async (name: string) => {
    setSaving(true);
    try {
      await api.createFilter({ name, board_id: boardId, query: filters });
      closeSave();
      await load();
    } finally {
      setSaving(false);
    }
  };
  const del = async (id: string) => {
    await api.deleteFilter(id);
    await load();
  };

  return (
    <div ref={ref} className="relative">
      <Button ref={triggerRef} variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        Views{views.length ? ` (${views.length})` : ''}
      </Button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-1.5 min-w-[240px] rounded-xl border border-line bg-surface-raised p-1.5 shadow-2xl shadow-black/60">
          {views.map((v) => (
            <div key={v.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  onApply(sanitizeFilters(v.query));
                  setOpen(false);
                }}
                className={cn('flex min-h-[48px] flex-1 items-center rounded-lg px-2.5 text-left text-[13px] text-fg-soft transition-colors hover:bg-layer-strong')}
              >
                {v.name}
              </button>
              <button
                type="button"
                onClick={() => del(v.id)}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-fg-faint transition-colors hover:bg-layer-strong hover:text-danger"
                aria-label="Delete view"
              >
                ✕
              </button>
            </div>
          ))}
          {views.length === 0 ? <div className="px-2.5 py-2 text-[12px] text-fg-faint">No saved views.</div> : null}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setSaveOpen(true);
            }}
            className="mt-1 flex min-h-[48px] w-full items-center rounded-lg border-t border-line px-2.5 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
          >
            Save current view…
          </button>
        </div>
      ) : null}
      <PromptDialog
        open={saveOpen}
        onClose={closeSave}
        onSubmit={save}
        title="Save view"
        label="Name this view"
        placeholder="e.g. My open items"
        submitLabel="Save"
        busy={saving}
      />
    </div>
  );
}
